/**
 * Decision paths: the leaves of `Decisions` ("loans.cre.growth", "deposits.retailMm.rate.front", "capital.buyback.pauseOnOutlook"), each one a setting a
 * player can move on its own. Everything that enumerates, compares or mutates a decision by path (the decision twins, the family reverts, the CRO memo)
 * goes through here. A path is also its field path in `Decisions`, so reading and writing is a walk down the object.
 */
import { z } from "zod";
import { canonicalJson, DecisionFamily, DecisionId, Decisions, InvariantViolation, type DecisionPath } from "./cp1_domain";

function leavesOf(schema: z.ZodType, prefix: string): string[] {
  return schema instanceof z.ZodObject
    ? Object.entries(schema.shape as Record<string, z.ZodType>).flatMap(([key, child]) => leavesOf(child, prefix === "" ? key : `${prefix}.${key}`))
    : [prefix];
}

/** Every leaf path of `Decisions`, in schema (tree) order. */
export const LEAF_PATHS: readonly DecisionPath[] = leavesOf(Decisions, "");

/** The decision a path belongs to ("deposits.retailMm.rate.front" → "deposits.retailMm.rate"). */
export const decisionOfPath = (path: string): DecisionId => {
  const id = DecisionId.options.find((d) => path === d || path.startsWith(`${d}.`));
  if (id === undefined) throw new InvariantViolation("DECISION_PATH", `not a decision path: ${path}`);
  return id;
};

/** The family a path belongs to (rationale and revert-one group by it): its first segment. */
export const familyOfPath = (path: string): DecisionFamily => DecisionFamily.parse(path.split(".")[0]);

/** The value at a path of `decisions`. */
export const decisionValueAt = (decisions: Decisions, path: string): unknown =>
  path.split(".").reduce<unknown>((node, key) => (node as Record<string, unknown>)[key], decisions);

/** `decisions` with the one leaf at `path` set to `value` (a copy; the shape is the caller's to keep valid). */
export function withDecisionPath(decisions: Decisions, path: string, value: unknown): Decisions {
  const set = (node: unknown, keys: readonly string[]): unknown => {
    const [head = "", ...rest] = keys;
    const obj = node as Record<string, unknown>;
    return { ...obj, [head]: rest.length === 0 ? value : set(obj[head], rest) };
  };
  return set(decisions, path.split(".")) as Decisions;
}

/** Every leaf path where `decisions` differs from the standing (held) decisions `standing`, in `LEAF_PATHS` order. */
export const decisionPaths = (decisions: Decisions, standing: Decisions): DecisionPath[] =>
  LEAF_PATHS.filter((p) => canonicalJson(decisionValueAt(decisions, p)) !== canonicalJson(decisionValueAt(standing, p)));

/** `decisions` with every path of `family` set back to `standing`'s value. */
export const revertFamily = (decisions: Decisions, standing: Decisions, family: DecisionFamily): Decisions =>
  LEAF_PATHS.filter((p) => familyOfPath(p) === family).reduce((acc, p) => withDecisionPath(acc, p, decisionValueAt(standing, p)), decisions);
